import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest, fetchJson, ErroApi } from "@/lib/queryClient";
import type {
  EnviarTesteDTO,
  EnviarTesteInput,
  EnviosDTO,
  ErrosDeTextoDTO,
  FiltroEnvio,
  MensagensResumoDTO,
  MeusAvisosDTO,
  ModeloDetalheDTO,
  PatchRegrasAviso,
  PublicarAvisoInput,
  RegrasAvisoDTO,
} from "@shared/avisos-admin";
import type { AvisoCategoria } from "@shared/avisos";

/**
 * Plataforma › Mensagens WhatsApp e Meus avisos. Toda `queryFn` usa `fetchJson`
 * (checa `r.ok`): o 404 de quem não é da plataforma vira erro, não "dado".
 */
const BASE = "/api/plataforma/avisos";

export function useModelosAviso() {
  return useQuery<MensagensResumoDTO>({
    queryKey: [BASE, "modelos"],
    queryFn: () => fetchJson<MensagensResumoDTO>(`${BASE}/modelos`),
  });
}

export function useModeloAviso(chave: string | undefined) {
  return useQuery<ModeloDetalheDTO>({
    queryKey: [BASE, "modelos", chave],
    queryFn: () => fetchJson<ModeloDetalheDTO>(`${BASE}/modelos/${chave}`),
    enabled: !!chave,
  });
}

export function useEnviosAviso(status: FiltroEnvio, q: string) {
  const params = new URLSearchParams();
  if (status !== "todos") params.set("status", status);
  if (q.trim()) params.set("q", q.trim());
  const qs = params.toString();
  return useQuery<EnviosDTO>({
    queryKey: [BASE, "envios", status, q.trim()],
    queryFn: () => fetchJson<EnviosDTO>(`${BASE}/envios${qs ? `?${qs}` : ""}`),
  });
}

export function useRegrasAviso() {
  return useQuery<RegrasAvisoDTO>({
    queryKey: [BASE, "regras"],
    queryFn: () => fetchJson<RegrasAvisoDTO>(`${BASE}/regras`),
  });
}

function invalidar(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: [BASE] });
}

/**
 * O 422 do publicar traz a LISTA de erros (`erros.um` / `erros.grupo`). O
 * `ErroApi` guarda o corpo em texto; aqui ele vira objeto de novo para a tela
 * marcar cada verificação.
 */
export class ErroPublicar extends Error {
  constructor(readonly mensagem: string, readonly erros: ErrosDeTextoDTO | null) {
    super(mensagem);
  }
}

function comoErroPublicar(err: unknown): ErroPublicar {
  if (err instanceof ErroApi) {
    let erros: ErrosDeTextoDTO | null = null;
    const corpo = err.message.substring(err.message.indexOf(":") + 1).trim();
    try {
      const j = JSON.parse(corpo);
      erros = j && j.erros ? j.erros : null;
    } catch (parseErr) {
      // Corpo que não é JSON (502 de proxy): fica só a mensagem.
      console.warn("[avisos] corpo do erro não é JSON", parseErr);
    }
    return new ErroPublicar(err.mensagem, erros);
  }
  return new ErroPublicar(err instanceof Error ? err.message : "Não deu certo.", null);
}

export function usePublicarAviso(chave: string) {
  const qc = useQueryClient();
  return useMutation<{ versao: number }, ErroPublicar, PublicarAvisoInput>({
    mutationFn: async (input) => {
      try {
        return await (await apiRequest("POST", `${BASE}/modelos/${chave}/versoes`, input)).json();
      } catch (err) {
        throw comoErroPublicar(err);
      }
    },
    onSuccess: () => invalidar(qc),
  });
}

export function useRestaurarAviso(chave: string) {
  const qc = useQueryClient();
  return useMutation<{ versao: number }, Error, number>({
    mutationFn: async (versao) => (await apiRequest("POST", `${BASE}/modelos/${chave}/versoes/${versao}/restaurar`)).json(),
    onSuccess: () => invalidar(qc),
  });
}

export function usePausarAviso(chave: string) {
  const qc = useQueryClient();
  return useMutation<{ pausado: boolean }, Error, boolean>({
    mutationFn: async (pausado) => (await apiRequest("POST", `${BASE}/modelos/${chave}/pausa`, { pausado })).json(),
    onSuccess: () => invalidar(qc),
  });
}

export function useTesteAviso(chave: string) {
  return useMutation<EnviarTesteDTO, ErroPublicar, EnviarTesteInput>({
    mutationFn: async (input) => {
      try {
        return await (await apiRequest("POST", `${BASE}/modelos/${chave}/teste`, input)).json();
      } catch (err) {
        throw comoErroPublicar(err);
      }
    },
  });
}

export function useTextoDoEnvio(id: string | null) {
  return useQuery<{ texto: string; enviado: boolean }>({
    queryKey: [BASE, "envios", "texto", id],
    queryFn: () => fetchJson(`${BASE}/envios/${id}/texto`),
    enabled: !!id,
  });
}

export function useTentarDeNovo() {
  const qc = useQueryClient();
  return useMutation<{ reenfileiradas: number }, Error, string>({
    mutationFn: async (id) => (await apiRequest("POST", `${BASE}/envios/${id}/tentar-de-novo`)).json(),
    onSuccess: () => invalidar(qc),
  });
}

export function useSalvarRegrasAviso() {
  const qc = useQueryClient();
  return useMutation<RegrasAvisoDTO, Error, PatchRegrasAviso>({
    mutationFn: async (patch) => (await apiRequest("PATCH", `${BASE}/regras`, patch)).json(),
    onSuccess: (dto) => {
      qc.setQueryData([BASE, "regras"], dto);
      invalidar(qc);
    },
  });
}

// ─── Meus avisos ─────────────────────────────────────────────────────────────

const CONTA = "/api/conta/avisos";

export function useMeusAvisos() {
  return useQuery<MeusAvisosDTO>({
    queryKey: [CONTA],
    queryFn: () => fetchJson<MeusAvisosDTO>(CONTA),
  });
}

export function useDefinirMeuAviso() {
  const qc = useQueryClient();
  return useMutation<MeusAvisosDTO, Error, { categoria: AvisoCategoria; ativo: boolean }>({
    mutationFn: async ({ categoria, ativo }) => (await apiRequest("PUT", `${CONTA}/${categoria}`, { ativo })).json(),
    onSuccess: (dto) => qc.setQueryData([CONTA], dto),
  });
}
